/**
 * Pseudo-OAuth con elevauth: login redirige a `${elevauthUrl}/auth/login`, elevauth vuelve con
 * `?token=<jwt>` y lo guardamos en localStorage. Mismo patrón que el resto de servicios de Eleva.
 * elevauth exige que el host del callback esté en AUTH_CALLBACK_URL_ALLOWED_HOSTS.
 */
export const TOKEN_KEY = 'odd:token';

export const tokenStorage = {
  get: (): string | null => {
    try {
      return localStorage.getItem(TOKEN_KEY);
    } catch {
      return null;
    }
  },
  set: (token: string) => {
    try {
      localStorage.setItem(TOKEN_KEY, token);
    } catch {
      /* almacenamiento bloqueado */
    }
  },
  clear: () => {
    try {
      localStorage.removeItem(TOKEN_KEY);
    } catch {
      /* almacenamiento bloqueado */
    }
  },
};

/** Último rechazo de la API (401): motivo y datos no sensibles del token, para la pantalla de login */
export interface AuthErrorInfo {
  at: string;
  path: string;
  reason: string;
  message: string;
  claims: unknown;
}
const AUTH_ERROR_KEY = 'odd:auth-error';
export const authErrorStorage = {
  get: (): AuthErrorInfo | null => {
    try {
      const v = sessionStorage.getItem(AUTH_ERROR_KEY);
      return v ? (JSON.parse(v) as AuthErrorInfo) : null;
    } catch {
      return null;
    }
  },
  set: (e: AuthErrorInfo) => {
    try {
      sessionStorage.setItem(AUTH_ERROR_KEY, JSON.stringify(e));
    } catch {
      /* almacenamiento bloqueado */
    }
  },
  clear: () => {
    try {
      sessionStorage.removeItem(AUTH_ERROR_KEY);
    } catch {
      /* almacenamiento bloqueado */
    }
  },
};

/** Guarda el `?token=` de la URL (si viene) y lo quita de la barra de direcciones. */
export function consumeTokenFromUrl(loc: Location = window.location, history: History = window.history) {
  const url = new URL(loc.href);
  const token = url.searchParams.get('token');
  if (!token) return false;
  tokenStorage.set(token);
  authErrorStorage.clear();
  console.info('[auth] token recibido de elevauth', describeToken(token));
  url.searchParams.delete('token');
  history.replaceState(null, '', url);
  return true;
}

/** Datos legibles del JWT (sin verificar; la firma la comprueba la API) para depurar en consola */
export function describeToken(token: string) {
  try {
    const p = JSON.parse(atob(token.split('.')[1]!.replace(/-/g, '+').replace(/_/g, '/'))) as {
      domain?: { slug?: string };
      agent?: { email?: string };
      exp?: number;
    };
    return {
      domain: p.domain?.slug,
      email: p.agent?.email,
      exp: p.exp ? new Date(p.exp * 1000).toISOString() : undefined,
    };
  } catch {
    return { malformed: true };
  }
}

const trim = (u: string) => u.replace(/\/+$/, '');

export function loginUrl(elevauthUrl: string, domain: string, callbackUrl: string): string {
  const url = new URL(`${trim(elevauthUrl)}/auth/login`);
  url.searchParams.set('callback_url', callbackUrl);
  url.searchParams.set('domain', domain);
  return url.toString();
}

export function login(elevauthUrl: string, domain: string) {
  window.location.assign(loginUrl(elevauthUrl, domain, window.location.origin + window.location.pathname));
}

/** elevauth borra su cookie y redirige a su AUTH_DASHBOARD_REDIRECT_URL. */
export function logout(elevauthUrl: string) {
  tokenStorage.clear();
  window.location.assign(`${trim(elevauthUrl)}/auth/logout`);
}
